
import React, { useState } from 'react';

const ContactPage: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    const formElement = e.currentTarget;
    const formData = new FormData(formElement);
    const payload = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('https://services.leadconnectorhq.com/hooks/PVkVu7wIaqwYQyJ9TFYu/webhook-trigger/fa531bb8-63ff-4b23-8dac-144f4fea6686', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setIsSuccess(true);
      } else {
        console.error('System Error: Webhook transmission failed.', response.statusText);
        setIsSuccess(true);
      }
    } catch (error) {
      console.error('Network Error: Connectivity protocol failure.', error);
      setIsSuccess(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-32 sm:pt-52 pb-16 sm:pb-32 px-4 sm:px-6 bg-[#050505] relative min-h-screen">
      <div className="glow-spot top-0 right-0 opacity-20"></div>
      <div className="container mx-auto max-w-6xl">
        <div className="grid lg:grid-cols-2 gap-16 sm:gap-24 items-start">
          <div className="space-y-8 sm:space-y-12">
            <header className="space-y-4 sm:space-y-6">
              <div className="mono text-[9px] sm:text-[10px] font-bold text-[#32dee6] tracking-[0.3em] sm:tracking-[0.4em] uppercase">GET_STARTED</div>
              <h1 className="text-5xl sm:text-7xl md:text-8xl font-black tracking-tighter text-white leading-[0.85] uppercase">
                Initialize <br />
                <span className="gradient-text">Plan.</span>
              </h1>
              <p className="text-lg sm:text-2xl text-gray-400 font-light leading-relaxed">
                Stop losing leads. We'll find exactly where your business is losing money and show you how to fix it with simple AI tools.
              </p>
            </header>

            <div className="space-y-6 sm:space-y-8">
              <div className="p-6 sm:p-8 rounded-[1.5rem] sm:rounded-[2rem] glass-card border border-white/5 space-y-4">
                <h4 className="mono text-[9px] sm:text-[10px] font-bold text-white uppercase tracking-[0.3em] sm:tracking-[0.4em]">WHAT'S_INCLUDED</h4>
                <ul className="space-y-3 sm:space-y-4">
                  {[
                    "Find where leads are slipping through",
                    "Custom AI Voice core logic design",
                    "SMS follow-up sequence blueprint",
                    "Full technical infrastructure audit"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center space-x-3 text-gray-400">
                      <div className="w-1 h-1 rounded-full bg-[#32dee6]"></div>
                      <span className="text-[10px] sm:text-sm uppercase tracking-widest">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <p className="text-xs sm:text-sm text-gray-600 italic">"No sales pitch. Just a clear look at how to grow your business with AI."</p>
            </div>
          </div>

          <div className="glass-card p-8 sm:p-12 rounded-[2.5rem] sm:rounded-[4rem] border-white/10 shadow-2xl relative z-10">
            {isSuccess ? (
              <div className="text-center py-10 sm:py-20 space-y-6 sm:space-y-8 animate-fade-in">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#32dee6]/10 border border-[#32dee6]/30 rounded-full flex items-center justify-center mx-auto">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#32dee6" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                </div>
                <div className="space-y-2 sm:space-y-4">
                  <h3 className="text-2xl sm:text-3xl font-bold text-white uppercase tracking-tight">Plan Initialized</h3>
                  <p className="text-gray-400 mono text-[9px] sm:text-[10px] tracking-widest uppercase">We will be in touch shortly.</p>
                </div>
                <button 
                  onClick={() => setIsSuccess(false)}
                  className="px-6 py-3 sm:px-8 sm:py-4 bg-white/5 border border-white/10 text-white mono text-[9px] sm:text-[10px] font-bold uppercase tracking-widest rounded-xl hover:bg-white hover:text-black transition-all"
                >
                  Close
                </button>
              </div>
            ) : (
              <form className="space-y-6 sm:space-y-8" onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 gap-6 sm:gap-8">
                  <div className="space-y-1 sm:space-y-2">
                    <label className="mono text-[8px] sm:text-[9px] font-bold text-gray-500 uppercase tracking-widest ml-4">Full_Name</label>
                    <input required name="fullName" type="text" placeholder="John Doe" className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 sm:px-8 py-4 sm:py-5 text-white placeholder:text-white/20 focus:ring-2 focus:ring-[#32dee6] outline-none transition-all text-sm sm:text-base" />
                  </div>
                  <div className="space-y-1 sm:space-y-2">
                    <label className="mono text-[8px] sm:text-[9px] font-bold text-gray-500 uppercase tracking-widest ml-4">Business_Name</label>
                    <input required name="businessName" type="text" placeholder="Acme Corp" className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 sm:px-8 py-4 sm:py-5 text-white placeholder:text-white/20 focus:ring-2 focus:ring-[#32dee6] outline-none transition-all text-sm sm:text-base" />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-6 sm:gap-8">
                  <div className="space-y-1 sm:space-y-2">
                    <label className="mono text-[8px] sm:text-[9px] font-bold text-gray-500 uppercase tracking-widest ml-4">Business_Email</label>
                    <input required name="email" type="email" placeholder="john@company.com" className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 sm:px-8 py-4 sm:py-5 text-white placeholder:text-white/20 focus:ring-2 focus:ring-[#32dee6] outline-none transition-all text-sm sm:text-base" />
                  </div>
                  <div className="space-y-1 sm:space-y-2">
                    <label className="mono text-[8px] sm:text-[9px] font-bold text-gray-500 uppercase tracking-widest ml-4">Phone_Number</label>
                    <input required name="phone" type="tel" placeholder="+61 000 000" className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 sm:px-8 py-4 sm:py-5 text-white placeholder:text-white/20 focus:ring-2 focus:ring-[#32dee6] outline-none transition-all text-sm sm:text-base" />
                  </div>
                </div>

                <div className="space-y-1 sm:space-y-2">
                  <label className="mono text-[8px] sm:text-[9px] font-bold text-gray-500 uppercase tracking-widest ml-4">Monthly_Lead_Volume</label>
                  <input required name="leadVolume" type="text" placeholder="e.g. 50-100 leads" className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 sm:px-8 py-4 sm:py-5 text-white placeholder:text-white/20 focus:ring-2 focus:ring-[#32dee6] outline-none transition-all text-sm sm:text-base" />
                </div>
                <button 
                  disabled={isSubmitting}
                  type="submit"
                  className={`w-full py-5 sm:py-6 font-black uppercase tracking-widest text-xs sm:text-sm rounded-2xl transition-all transform shadow-[0_20px_40px_rgba(255,255,255,0.05)] flex items-center justify-center space-x-3 ${isSubmitting ? 'bg-gray-800 text-gray-400 cursor-not-allowed' : 'bg-white text-black hover:bg-[#32dee6] hover:-translate-y-1'}`}
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                      <span>Initializing...</span>
                    </>
                  ) : (
                    <span>Initialize Protocols</span>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
